import React, { useState, useEffect } from 'react';
import IconSvg from '../../../img/trip_planner/steps/step_6.svg';
import useQuestionTrip from "../../../../../hooks/useQuestionTrip";

interface Step6DestinationProps {
    onStepValidation: (isValid: boolean) => void;
    onUpdateTripProfile: (answerId: number | null) => void;
    initialSelectedId?: number | null;
}

const budgetOptions = [
    { id: 501, name: "Low ($0 - $1000 USD)" },
    { id: 502, name: "Medium ($1000 - $2500 USD)" },
    { id: 503, name: "High ($2500+ USD)" }
];

const Step6Destination = ({ onStepValidation, onUpdateTripProfile, initialSelectedId }: Step6DestinationProps) => {
    const [selectedBudget, setSelectedBudget] = useState<number | null>(initialSelectedId ?? null);
    const { questionsTrip } = useQuestionTrip();
    const [isInitialized, setIsInitialized] = useState(false);

    // Initialize with existing answerId when editing a trip
    useEffect(() => {
        if (!isInitialized && initialSelectedId) {
            setSelectedBudget(initialSelectedId);
            setIsInitialized(true);
        }
    }, [initialSelectedId, isInitialized]);

    useEffect(() => {
        onStepValidation(selectedBudget !== null);
    }, [selectedBudget, onStepValidation]);

    // Only update trip profile after initialization to prevent wiping existing data
    useEffect(() => {
        if (!isInitialized) return;

        const debouncedUpdate = setTimeout(() => {
            onUpdateTripProfile(selectedBudget);
        }, 100);

        return () => clearTimeout(debouncedUpdate);
    }, [selectedBudget, onUpdateTripProfile, isInitialized]);

    return (
        <div className="space-y-6">
            <div className="text-center mb-8">
                <div className="w-20-h mx-auto mb-4 relative">
                    <img
                        src={IconSvg}
                        alt="What's your budget?"
                        className="w-full h-full object-contain"
                    />
                    <div className="absolute inset-0" style={{ backgroundColor: 'var(--primary-color)', mixBlendMode: 'lighten' }} />
                </div>
                <h2 className="text-2xl font-semibold mb-2 syne-fm">What's your budget?</h2>
            </div>

            <div className="max-w-md mx-auto space-y-3">
                {budgetOptions.map(option => (
                    <label
                        key={option.id}
                        className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all duration-200 accent-[var(--primary-color)] ${
                            selectedBudget === option.id
                                ? 'border-primary-color bg-primary-color-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                    >
                        <input
                            type="radio"
                            name="budget-option"
                            checked={selectedBudget === option.id}
                            onChange={() => {
                                if (!isInitialized) setIsInitialized(true);
                                setSelectedBudget(option.id);
                            }}
                            className="w-5 h-5 text-primary-color focus:ring-primary-color border-gray-300"
                        />
                        <span className={`text-base ${
                            selectedBudget === option.id ? 'text-primary-color font-medium' : 'text-gray-700'
                        }`}>
                            {option.name}
                        </span>
                    </label>
                ))}
            </div>
        </div>
    );
};

export default Step6Destination;
